import React, { useState, useContext, useEffect, useRef } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  List,
  ListItem,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Slider,
  Grid,
  Avatar,
  Box,
  AppBar,
  Toolbar,
  Tooltip,
  Popover,
  Switch,
} from "@material-ui/core";

import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import PaletteOutlinedIcon from "@material-ui/icons/PaletteOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import SearchIcon from "@material-ui/icons/Search";
import CommandPalette from "../components/CommandPalette";
import useUiMode from "../hooks/useUiMode";
import MenuIcon from "@material-ui/icons/Menu";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import AccordionMenu from "../components/AccordionMenu";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import useAuth from "../hooks/useAuth.js";
import BackdropLoading from "../components/BackdropLoading";
import toastError from "../errors/toastError";

//import logo from "../assets/logo.png";
import { SocketContext } from "../context/Socket/SocketContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";

import { useWebInfo } from "../context/WebInfoContext";

import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import VolumeDownIcon from '@material-ui/icons/VolumeDown';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import LoggerConfig from "../components/LoggerConfig";
import SettingsIcon from '@material-ui/icons/Settings';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import { getVersionNumber } from "../config/version";
import { getProfilePicUrl } from "../services/api";
import { getInitials } from "../helpers/getInitials";
import { generateColor } from "../helpers/colorGenerator";
import AnnouncementsOnLogin from "../components/AnnouncementsOnLogin";
import UpdateVersionModal from "../components/UpdateVersionModal";
import { SELF_UPDATE_ENABLED } from "../config/features";
import ChatPopover from "../pages/Chat/ChatPopover";
import WhatsAppConnectionsMenu from "../components/WhatsAppConnectionsMenu";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import FlowAlertsListener from "../components/FlowBuilder/FlowAlertsListener";
import LanguageSelectorPopover from "../components/UserLanguageSelector";
import AuditModeBanner from "../components/AuditModeBanner";
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { useHistory, useLocation } from "react-router-dom";
import { getOnboardingConfig } from "../services/onboarding.api";
import { i18n } from "../translate/i18n";

// 0390 FT-6 — el ancho lo fija el tema activo: 280px en `classic`, 248px en
// `professional`. El valor de reserva mantiene el comportamiento actual si el
// tema no llegara a cargar.
const drawerWidth = 'var(--wtx-sidebar-width, 280px)';

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    maxHeight: "100vh",
    overflow: "hidden", // ✅ Evitar scroll en el root
    backgroundColor: theme.palette.fancyBackground,
    '& .MuiButton-outlinedPrimary': {
      color: theme.mode === 'light' ? '#FFF' : '#FFF',
	  backgroundColor: theme.mode === 'light' ? '#1e3a8a' : '#1f2937',
      //border: theme.mode === 'light' ? '1px solid rgba(0 124 102)' : '1px solid rgba(255, 255, 255, 0.5)',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.mode === 'light' ? '#1e3a8a' : '#FFF',
    }
  },
  avatar: {
    width: "100%",
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
    color: theme.palette.dark.main,
    background: theme.palette.barraSuperior,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
    minHeight: "48px",
    [theme.breakpoints.down("sm")]: {
      height: "48px"
    },
    '& img': {
      transition: 'opacity 0.3s ease',
    },
    '&.collapsed img': {
      opacity: 0,
      width: 0,
      height: 0,
      overflow: 'hidden',
    },
    '&.collapsed': {
      justifyContent: "center",
    }
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarFixed: {
    position: "fixed",
    top: 0,
    right: 0,
    left: drawerWidth,
    width: `calc(100% - ${drawerWidth})`,
    zIndex: theme.zIndex.drawer + 1,
    // 0390 FT-7 — superficie sólida del tema en `professional` (P5: nada de
    // gradientes en el cromo de la aplicación).
    backgroundColor: props => props.pro
      ? 'var(--wtx-surface, #ffffff)'
      : theme.palette.background.paper,
    borderBottom: props => props.pro
      ? '1px solid var(--wtx-border-subtle, #e0e0e0)'
      : `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    // P4: la barra se separa del contenido por una línea de 1px, no por sombra.
    // La sombra actual sugiere una elevación que la barra no tiene: está pegada.
    boxShadow: props => props.pro
      ? 'none'
      : (theme.mode === 'dark'
          ? '0 2px 8px rgba(0,0,0,0.3)'
          : '0 2px 8px rgba(0,0,0,0.1)'),
    overflow: "hidden", // ✅ Eliminar scroll de la AppBar
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      left: 0,
      width: "100%"
    }
  },
  /**
   * 0390 §8.1.1 — asomo del menú plegado.
   *
   * Va en SUPERPOSICIÓN, no empujando el contenido: si el área principal se
   * desplazara en cada pasada del puntero, el navegador recalcularía el diseño
   * completo una y otra vez, y la interfaz daría sensación de inestabilidad.
   *
   * Aquí la sombra sí está justificada (P4): es la única parte del cromo que
   * flota de verdad sobre el resto.
   */
  drawerPaperPeek: {
    position: "fixed !important",
    top: 0,
    bottom: 0,
    left: 0,
    height: "100vh",
    width: 'var(--wtx-sidebar-width, 248px) !important',
    zIndex: theme.zIndex.drawer + 2,
    boxShadow: 'var(--wtx-shadow-lg, 0 8px 24px rgba(0,0,0,0.25))',
    overflowX: "hidden",
  },
  appBarFixedClosed: {
    left: theme.spacing(7),
    width: `calc(100% - ${theme.spacing(7)}px)`,
    [theme.breakpoints.up("sm")]: {
      left: theme.spacing(9),
      width: `calc(100% - ${theme.spacing(9)}px)`,
    },
    [theme.breakpoints.down("sm")]: {
      left: 0,
      width: "100%"
    }
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth})`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  toolbarFixed: {
    minHeight: 'var(--wtx-topbar-height, 56px)',
    maxHeight: 'var(--wtx-topbar-height, 56px)',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between", // ✅ Cambiado para distribuir contenido
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: props => props.pro
      ? 'var(--wtx-surface, #ffffff)'
      : theme.palette.background.paper,
    overflow: "hidden", // ✅ Eliminar scroll innecesario
    '& .MuiIconButton-root': {
      color: theme.palette.text.primary + ' !important',
      '&:hover': {
        backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      }
    }
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: "none",
  },
  /** Botón hamburguesa móvil: visible solo en pantallas pequeñas */
  mobileMenuButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  title: {
    flexGrow: 1,
    fontSize: 14,
    color: "white",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    // ✅ CORREGIDO: Fondo dinámico del drawer según tema
    // 0390 FT-7 — en `professional` la superficie la fija el tema.
    backgroundColor: props => props.pro
      ? 'var(--wtx-surface, #ffffff)'
      : (theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper),
    borderRight: props => props.pro
      ? '1px solid var(--wtx-border-subtle, #e0e0e0)'
      : undefined,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeInOut, // ✅ TRANSICIÓN MÁS SUAVE
      duration: theme.transitions.duration.standard, // ✅ DURACIÓN ESTÁNDAR
    }),
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    },
    ...theme.scrollbarStylesSoft
  },
  drawerPaperClose: {
    overflowX: "hidden",
    // ✅ CORREGIDO: Fondo dinámico del drawer cerrado según tema
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeInOut, // ✅ TRANSICIÓN MÁS SUAVE
      duration: theme.transitions.duration.standard, // ✅ DURACIÓN ESTÁNDAR
    }),
    // 0390 FT-6 — ancho del rail desde el tema: 72px en `classic`, 56px en
    // `professional`. En pantallas menores que `sm` se conserva el valor histórico
    // de 56px (theme.spacing(7)), que ya era el más estrecho.
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: 'var(--wtx-sidebar-width-collapsed, 72px)',
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    height: 'calc(100vh - var(--wtx-topbar-height, 56px))', // ✅ Altura exacta menos la AppBar
    maxHeight: 'calc(100vh - var(--wtx-topbar-height, 56px))', // ✅ Evitar overflow
    overflowY: "auto", // ✅ Solo scroll vertical cuando sea necesario
    overflowX: "hidden", // ✅ Sin scroll horizontal
    display: "flex",
    flexDirection: "column",
    marginTop: 'var(--wtx-topbar-height, 56px)', // ✅ Espacio para la AppBar fija
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeInOut, // ✅ TRANSICIÓN SUAVE
      duration: theme.transitions.duration.standard, // ✅ DURACIÓN ESTÁNDAR
    }),
    // ✅ Ocultar scrollbar cuando no hay contenido que hacer scroll
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.mode === 'dark' ? '#4B5563' : '#cbd5e0',
      borderRadius: '4px',
      '&:hover': {
        background: theme.mode === 'dark' ? '#6B7280' : '#a0aec0',
      }
    },
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column"
  },
  containerWithScroll: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    '& .MuiListItemIcon-root': {
      minWidth: '40px',
      justifyContent: 'center',
      '& svg': {
        fontSize: '20px',
      },
    },
  },
  NotificationsPopOver: {
    // color: theme.barraSuperior.secondary.main,
  },
  logo: {
    width: "80%",
    height: "auto",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "80%",
      maxWidth: 180,
    },
    logo: theme.logo
  },
  /** Logo del drawer: en móvil no debe desbordar la barra superior del menú */
  drawerLogoImg: {
    margin: "0 auto",
    display: "block",
    width: "50%",
    maxWidth: 180,
    maxHeight: 52,
    height: "auto",
    objectFit: "contain",
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      maxWidth: "72%",
      maxHeight: 40,
    },
  },
}));

const LoggedInLayout = ({ children, themeToggle }) => {
  // 0390 FT-7 — el tema activo decide el aspecto de la barra superior. Se lee
  // aquí arriba porque `useStyles` lo necesita como propiedad.
  const { colorMode } = useContext(ColorModeContext);
  const pro = (colorMode && colorMode.navStyle) === 'flat';

  const classes = useStyles({ pro });
  const history = useHistory();
  const location = useLocation();
  const [userModalOpen, setUserModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [anchorEl, setAnchorEl] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [menuOpen, setMenuOpen] = useState(false);
  const [loggerConfigOpen, setLoggerConfigOpen] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const { isSimple: isSimpleUi } = useUiMode();
  const [volumeAnchor, setVolumeAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const closeUserMenu = () => setUserMenuAnchor(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    if (!isSimpleUi) return undefined;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSimpleUi]);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [updateVersionModalOpen, setUpdateVersionModalOpen] = useState(false);
  const { loading, mergeSessionUser, user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { handleLogout } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");

  /**
   * 0390 §8.1.1 — asomo del menú plegado.
   *
   * Tres estados, no dos: desplegado, riel, y asomo temporal. El asomo NO altera
   * la preferencia guardada; al retirar el puntero se vuelve al riel. Solo el
   * botón de plegado cambia el estado persistido.
   */
  const [peeking, setPeeking] = useState(false);
  const peekTimer = useRef(null);

  // Los retardos salen del tema: 150/300 ms en `professional`, 0 en `classic`.
  const tokenMs = (clave, porDefecto) => {
    const t = ((colorMode && colorMode.tokens) || []).find(x => x.tokenKey === clave);
    const n = t ? parseInt(t.tokenValue, 10) : NaN;
    return Number.isFinite(n) ? n : porDefecto;
  };

  // Sin puntero fino no hay asomo posible: en pantallas táctiles el riel se
  // despliega tocándolo, que es lo que ya hace hoy.
  const hayPuntero =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const peekActivo = pro && hayPuntero && !drawerOpen && drawerVariant === "permanent";

  const cancelarTemporizador = () => {
    if (peekTimer.current) {
      clearTimeout(peekTimer.current);
      peekTimer.current = null;
    }
  };

  const entrarEnMenu = () => {
    if (!peekActivo) return;
    cancelarTemporizador();
    // Retardo de intención: evita que se despliegue al cruzar el puntero de paso.
    peekTimer.current = setTimeout(() => setPeeking(true), tokenMs("delay-peek-open", 150));
  };

  const salirDelMenu = () => {
    cancelarTemporizador();
    // Tolerancia a una salida momentánea, para que no parpadee.
    peekTimer.current = setTimeout(() => setPeeking(false), tokenMs("delay-peek-close", 300));
  };

  // Si deja de darse alguna condición —se despliega el menú, se cambia de tema—
  // el asomo debe replegarse solo. Sin esto quedaría abierto para siempre.
  useEffect(() => {
    if (!peekActivo && peeking) {
      cancelarTemporizador();
      setPeeking(false);
    }
  }, [peekActivo, peeking]);

  useEffect(() => cancelarTemporizador, []);

  const menuDesplegado = drawerOpen || peeking;
  
  // const [dueDate, setDueDate] = useState("");



  const theme = useTheme();

  const [volume, setVolume] = useState(Number(localStorage.getItem("volume")) || 1);

  useEffect(() => {
    localStorage.setItem("volume", volume);
  }, [volume]);

  // FASE 6 — Rutas legacy /appointments y /schedules: si no hay perfil → redirigir a /agenda.
  // /agenda maneja su propio estado (welcome screen + wizard interno). No redirigir desde ahí.
  useEffect(() => {
    if (location.pathname === "/onboarding" || location.pathname === "/agenda") return;
    const agendaPaths = ["/appointments", "/schedules"];
    if (!agendaPaths.includes(location.pathname)) return;
    getOnboardingConfig().then(({ data }) => {
      if (!data.business) history.replace("/agenda");
    }).catch(() => {});
  }, [location.pathname, history]);

  const { loadWebInfo } = useWebInfo();

  //################### CODIGOS DE TESTE #########################################
  // useEffect(() => {
  //   navigator.getBattery().then((battery) => {
  //     console.log(`Battery Charging: ${battery.charging}`);
  //     console.log(`Battery Level: ${battery.level * 100}%`);
  //     console.log(`Charging Time: ${battery.chargingTime}`);
  //     console.log(`Discharging Time: ${battery.dischargingTime}`);
  //   })
  // }, []);

  // useEffect(() => {
  //   const geoLocation = navigator.geolocation

  //   geoLocation.getCurrentPosition((position) => {
  //     let lat = position.coords.latitude;
  //     let long = position.coords.longitude;

  //     console.log('latitude: ', lat)
  //     console.log('longitude: ', long)
  //   })
  // }, []);

  // useEffect(() => {
  //   const nucleos = window.navigator.hardwareConcurrency;

  //   console.log('Nucleos: ', nucleos)
  // }, []);

  // useEffect(() => {
  //   console.log('userAgent', navigator.userAgent)
  //   if (
  //     navigator.userAgent.match(/Android/i)
  //     || navigator.userAgent.match(/webOS/i)
  //     || navigator.userAgent.match(/iPhone/i)
  //     || navigator.userAgent.match(/iPad/i)
  //     || navigator.userAgent.match(/iPod/i)
  //     || navigator.userAgent.match(/BlackBerry/i)
  //     || navigator.userAgent.match(/Windows Phone/i)
  //   ) {
  //     console.log('é mobile ', true) //celular
  //   }
  //   else {
  //     console.log('não é mobile: ', false) //nao é celular
  //   }
  // }, []);
  //##############################################################################

  const socketManager = useContext(SocketContext);

  // ✅ NUEVO: Escuchar cambios en las configuraciones del sitio web
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleSettingsUpdate = (data) => {
      if (data.action === "update") {
        // Si se actualiza alguna configuración del sitio web, recargar la información
        if (['websiteTitle', 'websiteDescription', 'websiteKeyword'].includes(data.setting.key)) {
          loadWebInfo();
        }
      }
    };

    // ✅ LIMPIAR LISTENER ANTERIOR PARA EVITAR DUPLICADOS
    socket.off(`company-${companyId}-settings`, handleSettingsUpdate);
    socket.on(`company-${companyId}-settings`, handleSettingsUpdate);

    return () => {
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-settings`, handleSettingsUpdate);
      }
    };
  }, [socketManager, loadWebInfo]);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth > 1200) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    setDrawerVariant(isMobile ? "temporary" : "permanent");
  }, [isMobile]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");
    let timeoutId = null;

    const socket = socketManager.getSocket(companyId);

    const handleAuthEvent = (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        timeoutId = setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    };

    // ✅ LIMPIAR LISTENER ANTERIOR PARA EVITAR DUPLICADOS
    socket.off(`company-${companyId}-auth`, handleAuthEvent);
    socket.on(`company-${companyId}-auth`, handleAuthEvent);

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-auth`, handleAuthEvent);
        socket.off(`company-${companyId}-notification`);
        socket.off("ready");
        socket.off("connect");
        socket.off("disconnect");
      }
      // ✅ LIMPIAR INTERVAL Y TIMEOUT PARA EVITAR MEMORY LEAKS
      if (interval) {
        clearInterval(interval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [socketManager]);

  // eslint-disable-next-line no-unused-vars
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleLogoutClick = () => {
    handleLogout();
    handleCloseMenu();
  };

  const handleSettingsMenu = (event) => {
    setSettingsAnchorEl(event.currentTarget);
    setSettingsMenuOpen(true);
  };

  const handleCloseSettingsMenu = () => {
    setSettingsAnchorEl(null);
    setSettingsMenuOpen(false);
  };



  /** Cierre diferido en móvil para que RouterLink termine history.push antes de desmontar el menú */
  const drawerClose = () => {
    if (isMobile) {
      setTimeout(() => setDrawerOpen(false), 80);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  }

  if (loading) {
    return <BackdropLoading />;
  }
  
  // ✅ SOLUCIONADO: Logo sin Math.random() para evitar bucle infinito
  const logo = `${window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/public/logotipos/interno.png`;
  // Doc 0446: sin foto propia, el usuario lleva la foto de su conexión (Whatsapps.avatar,
  // que se guarda al conectar); si no tiene, la de la empresa, mejor si está conectada.
  const fotoValida = (u) => (u && !String(u).includes("nopicture.png") ? u : null);
  const conexionesConFoto = (whatsApps || []).filter((w) => fotoValida(w.avatar));
  const fotoDeConexion =
    fotoValida(conexionesConFoto.find((w) => w.id === user?.whatsappId)?.avatar) ||
    fotoValida(conexionesConFoto.find((w) => w.status === "CONNECTED")?.avatar) ||
    fotoValida(conexionesConFoto[0]?.avatar);
  const fotoUsuario = fotoValida(user?.profilePicUrl) || fotoDeConexion;
  const userPicSrc = fotoUsuario ? getProfilePicUrl(fotoUsuario) : null;

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose,
            // El asomo se superpone: el riel sigue ocupando su hueco y el área
            // principal no se mueve (doc 0390 §8.1.1).
            peeking && classes.drawerPaperPeek
          ),
        }}
        open={drawerOpen}
        onClose={drawerVariant === "temporary" ? handleDrawerClose : undefined}
        onMouseEnter={entrarEnMenu}
        onMouseLeave={salirDelMenu}
      >
        <div className={clsx(classes.toolbarIcon, !menuDesplegado && 'collapsed')}>
          {drawerOpen && (
            <img
              src={logo}
              className={classes.drawerLogoImg}
              alt={`${(typeof process !== 'undefined' && process.env) ? process.env.REACT_APP_NAME_SYSTEM : 'Sistema'}`}
            />
          )}
          <IconButton 
            onClick={() => setDrawerOpen(!drawerOpen)}
            style={{
              transition: 'transform 0.3s ease-in-out',
              transform: drawerOpen ? 'rotate(0deg)' : 'rotate(180deg)',
              marginLeft: drawerOpen ? 0 : 'auto',
              marginRight: drawerOpen ? 0 : 'auto',
            }}
            title={
              drawerOpen
                ? i18n.t("mainDrawer.appBar.tooltips.closeDrawer")
                : i18n.t("mainDrawer.appBar.tooltips.openDrawer")
            }
          >
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        {isSimpleUi && (
          <Box style={{ padding: menuDesplegado ? "10px 12px 2px" : "8px 4px 2px" }}>
            <ListItem
              button
              onClick={() => { drawerClose(); setPaletteOpen(true); }}
              aria-label={i18n.t("tickets.simple.searchPage")}
              style={{
                gap: 8,
                height: 40,
                borderRadius: 10,
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.secondary,
                justifyContent: menuDesplegado ? "flex-start" : "center",
                padding: menuDesplegado ? "0 10px" : 0,
              }}
            >
              <SearchIcon fontSize="small" />
              {menuDesplegado && (
                <>
                  <span style={{ flexGrow: 1, fontSize: 14 }}>{i18n.t("tickets.simple.search")}</span>
                  <span style={{ fontSize: 11, border: `1px solid ${theme.palette.divider}`, borderRadius: 6, padding: "1px 6px" }}>
                    Ctrl K
                  </span>
                </>
              )}
            </ListItem>
          </Box>
        )}
        <List className={classes.containerWithScroll}>
          <AccordionMenu drawerClose={drawerClose} drawerOpen={menuDesplegado} />
        </List>
        <Divider />
        {/* ✅ Información del usuario y versión movidas a la barra superior */}
        {/* Menú de configuración */}
        <Menu
          id="settings-menu"
          anchorEl={settingsAnchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={settingsMenuOpen}
          onClose={handleCloseSettingsMenu}
          PaperProps={{
            style: {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
              boxShadow: theme.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: '200px',
            },
          }}
        >
          <MenuItem 
            onClick={() => {
              toggleColorMode();
              handleCloseSettingsMenu();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.palette.background.paper;
            }}
            style={{ 
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            {theme.mode === 'dark' ? (
              <Brightness7Icon style={{ marginRight: 8, color: theme.palette.text.primary }} />
            ) : (
              <Brightness4Icon style={{ marginRight: 8, color: theme.palette.text.primary }} />
            )}
            {theme.mode === 'dark'
              ? i18n.t("mainDrawer.appBar.settingsMenu.lightMode")
              : i18n.t("mainDrawer.appBar.settingsMenu.darkMode")}
          </MenuItem>
          <MenuItem
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.palette.background.paper;
            }}
            style={{ 
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <Grid container spacing={2} alignItems="center" style={{ width: '100%' }}>
              <Grid item>
                <VolumeDownIcon style={{ color: theme.palette.text.primary }} />
              </Grid>
              <Grid item xs>
                <Slider
                  value={volume}
                  onChange={(e, newValue) => setVolume(newValue)}
                  min={0}
                  max={1}
                  step={0.1}
                  style={{ color: theme.palette.text.primary }}
                />
              </Grid>
              <Grid item>
                <VolumeUpIcon style={{ color: theme.palette.text.primary }} />
              </Grid>
            </Grid>
          </MenuItem>
        </Menu>
        {/* Modo simple (doc 0446): el usuario y sus ajustes, al pie del menú */}
        {isSimpleUi && (
          <>
            <ListItem
              button
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              aria-haspopup="menu"
              aria-label={user?.name || ""}
              style={{
                gap: 10,
                padding: drawerOpen ? "10px 14px" : "8px 4px",
                justifyContent: drawerOpen ? "flex-start" : "center",
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Avatar
                src={userPicSrc}
                style={{
                  width: 34,
                  height: 34,
                  backgroundColor: generateColor(user?.name || ""),
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
              >
                {getInitials(user?.name || "")}
              </Avatar>
              {drawerOpen && (
                <Box style={{ display: "flex", flexDirection: "column", minWidth: 0, flexGrow: 1 }}>
                  <Typography noWrap style={{ fontSize: 14, fontWeight: 600, color: theme.palette.text.primary }}>
                    {user?.name}
                  </Typography>
                  <Typography noWrap style={{ fontSize: 12, color: theme.palette.text.secondary }}>
                    {user?.email}
                  </Typography>
                </Box>
              )}
              {drawerOpen && <ExpandLessIcon fontSize="small" style={{ color: theme.palette.text.secondary }} />}
            </ListItem>
            {/* Menú de usuario (doc 0447, D9): cabecera con la persona, modo oscuro como interruptor y
                acceso al tema. Referencia: el menú de usuario de Whaticket. */}
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={closeUserMenu}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
              PaperProps={{ style: { minWidth: 280, borderRadius: 12, boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)" } }}
              MenuListProps={{ style: { paddingTop: 0 } }}
            >
              <Box style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px 12px" }}>
                <Avatar
                  src={userPicSrc}
                  style={{ width: 40, height: 40, backgroundColor: generateColor(user?.name || ""), color: "#fff", fontWeight: "bold", fontSize: 14 }}
                >
                  {getInitials(user?.name || "")}
                </Avatar>
                <Box style={{ minWidth: 0 }}>
                  <Typography noWrap style={{ fontSize: 14, fontWeight: 700, color: theme.palette.text.primary }}>{user?.name}</Typography>
                  <Typography noWrap style={{ fontSize: 12, color: theme.palette.text.secondary }}>{user?.email}</Typography>
                </Box>
              </Box>
              <Divider style={{ marginBottom: 6 }} />
              <MenuItem onClick={() => { closeUserMenu(); handleOpenUserModal(); }}>
                <PersonOutlineIcon fontSize="small" style={{ marginRight: 12 }} />
                {i18n.t("tickets.simple.profile")}
              </MenuItem>
              <LanguageSelectorPopover asMenuItem label={i18n.t("tickets.simple.language")} onDone={closeUserMenu} />
              {(user?.super || user?.profile === "admin") && (
                <MenuItem onClick={() => { closeUserMenu(); history.push("/settings?tab=appearance"); }}>
                  <PaletteOutlinedIcon fontSize="small" style={{ marginRight: 12 }} />
                  {i18n.t("tickets.simple.colorTheme")}
                </MenuItem>
              )}
              <MenuItem onClick={toggleColorMode} role="menuitemcheckbox" aria-checked={theme.mode === "dark"}>
                {theme.mode === "dark" ? (
                  <Brightness7Icon fontSize="small" style={{ marginRight: 12 }} />
                ) : (
                  <Brightness4Icon fontSize="small" style={{ marginRight: 12 }} />
                )}
                <span style={{ flexGrow: 1 }}>{i18n.t("tickets.simple.darkMode")}</span>
                <Switch size="small" color="primary" checked={theme.mode === "dark"} tabIndex={-1} style={{ pointerEvents: "none" }} />
              </MenuItem>
              <MenuItem onClick={(e) => setVolumeAnchor(e.currentTarget)} aria-haspopup="true">
                {volume > 0 ? (
                  <VolumeUpIcon fontSize="small" style={{ marginRight: 12 }} />
                ) : (
                  <VolumeOffIcon fontSize="small" style={{ marginRight: 12 }} />
                )}
                <span style={{ flexGrow: 1 }}>{i18n.t("tickets.simple.volume")}</span>
                <span style={{ fontSize: 12, color: theme.palette.text.secondary }}>{Math.round(volume * 100)}%</span>
              </MenuItem>
              <MenuItem onClick={() => { closeUserMenu(); history.push("/helps"); }}>
                <HelpOutlineIcon fontSize="small" style={{ marginRight: 12 }} />
                {i18n.t("mainDrawer.appBar.tooltips.help")}
              </MenuItem>
              <Divider style={{ margin: "6px 0" }} />
              <MenuItem onClick={() => { closeUserMenu(); handleLogoutClick(); }} style={{ color: theme.palette.error.main }}>
                <ExitToAppIcon fontSize="small" style={{ marginRight: 12 }} />
                {i18n.t("mainDrawer.appBar.tooltips.logout")}
              </MenuItem>
            </Menu>
            <Popover
              open={Boolean(volumeAnchor)}
              anchorEl={volumeAnchor}
              onClose={() => setVolumeAnchor(null)}
              anchorOrigin={{ vertical: "center", horizontal: "right" }}
              transformOrigin={{ vertical: "center", horizontal: "left" }}
              PaperProps={{ style: { display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", width: 240 } }}
            >
              <VolumeDownIcon fontSize="small" aria-hidden="true" />
              <Slider
                value={volume}
                onChange={(e, newValue) => setVolume(newValue)}
                min={0}
                max={1}
                step={0.1}
                aria-label={i18n.t("tickets.simple.volume")}
              />
              <VolumeUpIcon fontSize="small" aria-hidden="true" />
            </Popover>
          </>
        )}
        {/* Versión en la parte inferior del drawer - Fuera del área de scroll */}
        <Box style={{ 
          padding: drawerOpen ? '8px 12px' : '4px 2px',
          textAlign: 'center',
          borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`
        }}>
          <Typography style={{ 
            fontSize: drawerOpen ? "10px" : "8px", 
            fontWeight: "bold",
            color: "#df3333",
            lineHeight: drawerOpen ? "normal" : "1.2",
            wordBreak: drawerOpen ? "normal" : "break-word",
            overflow: drawerOpen ? "visible" : "hidden",
            textOverflow: drawerOpen ? "clip" : "ellipsis",
          }}>
            {drawerOpen
              ? i18n.t("mainDrawer.appBar.versionLong", {
                  version: getVersionNumber(),
                })
              : i18n.t("mainDrawer.appBar.versionShort", {
                  version: getVersionNumber(),
                })}
          </Typography>
        </Box>
      </Drawer>
      {/* ✅ BARRA SUPERIOR FIJA - Restaurada para mostrar notificaciones */}
      <AppBar 
        position="fixed" 
        className={clsx(classes.appBarFixed, !drawerOpen && classes.appBarFixedClosed)}
      >
        <Toolbar className={classes.toolbarFixed}>
          {isMobile && (
            <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.navigationMenu")} arrow>
              <IconButton
                edge="start"
                className={classes.mobileMenuButton}
                onClick={() => setDrawerOpen(true)}
                aria-label={i18n.t("mainDrawer.appBar.tooltips.openNavigationMenu")}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          <Box style={{ flex: 1 }} /> {/* Espaciador a la izquierda */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            {/* Estado global de conexiones WhatsApp: solo administración */}
            {user?.profile === "admin" && <WhatsAppConnectionsMenu />}
            {/* Modo simple: chat interno y ayuda pasan a «Más» y al menú del usuario */}
            {!isSimpleUi && (<>
            {/* Chat Interno */}
            <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.internalChat")} arrow>
              <span>
                <ChatPopover />
              </span>
            </Tooltip>
            
            {/* Ayuda */}
            <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.help")} arrow>
              <IconButton
                onClick={() => history.push("/helps")}
                size="small"
                style={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.palette.primary.main;
                  e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = theme.palette.text.primary;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            </>)}

            {/* Avisos en vivo de flujos rotos, solo administradores (0422 R5) */}
            <FlowAlertsListener />

            {/* Anuncios */}
            <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.announcements")} arrow>
              <span>
                <AnnouncementsPopover />
              </span>
            </Tooltip>
            
            {/* Notificaciones de WhatsApp */}
            <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.whatsappNotifications")} arrow>
              <span>
                <NotificationsPopOver volume={volume} customStyle={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                }} />
              </span>
            </Tooltip>
            
            {/* Modo simple (doc 0446): idioma, ajustes, salir y usuario viven en el menú del pie lateral */}
            {!isSimpleUi && (<>
            {/* Idioma del panel */}
            <LanguageSelectorPopover />

            {/* Configuración */}
            <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.settings")} arrow>
              <IconButton
                onClick={handleSettingsMenu}
                size="small"
                style={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.palette.text.primary;
                  e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = theme.palette.text.primary;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Actualizar Versión - Solo para usuarios con companyId: 1 y super: true */}
            {SELF_UPDATE_ENABLED && user?.companyId === 1 && user?.super === true && (
              <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.updateVersion")} arrow>
                <IconButton
                  onClick={() => setUpdateVersionModalOpen(true)}
                  size="small"
                  style={{
                    padding: '4px',
                    color: theme.palette.text.primary,
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = theme.palette.primary.main;
                    e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme.palette.text.primary;
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <SystemUpdateAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {/* Salir */}
            <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.logout")} arrow>
              <IconButton
                onClick={handleLogoutClick}
                size="small"
                style={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.palette.error.main;
                  e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = theme.palette.text.primary;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <ExitToAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Separador visual */}
            <Box style={{ 
              width: '1px', 
              height: '32px', 
              backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
              margin: `0 ${theme.spacing(0.5)}px`
            }} />
            
            {/* Información del Usuario - Lado Derecho */}
            <Box style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginLeft: theme.spacing(0.5) }}>
              <Avatar
                src={userPicSrc}
                onClick={handleOpenUserModal}
                style={{
                  width: 32,
                  height: 32,
                  backgroundColor: generateColor(user?.name || ""),
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "12px",
                  border: `2px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
                  boxShadow: theme.mode === 'dark' 
                    ? '0 2px 6px rgba(0,0,0,0.2)' 
                    : '0 2px 6px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onError={(e) => {
                  if (e.target) {
                    e.target.style.display = 'none';
                  }
                }}
              >
                {getInitials(user?.name || "")}
              </Avatar>
              {drawerOpen && (
                <Box style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography 
                    style={{ 
                      fontSize: "11px",
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      lineHeight: 1.2,
                    }}
                  >
                    {i18n.t("mainDrawer.appBar.user.label")}
                  </Typography>
                  {user?.name && (
                    <Typography 
                      style={{ 
                        fontSize: "13px",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        lineHeight: 1.2,
                        marginTop: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '120px',
                      }}
                    >
                      {user.name}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            </>)}
          </Box>
        </Toolbar>
      </AppBar>
      {isSimpleUi && <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />}
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <LoggerConfig
        open={loggerConfigOpen}
        onClose={() => setLoggerConfigOpen(false)}
      />
      {SELF_UPDATE_ENABLED && (
        <UpdateVersionModal
          open={updateVersionModalOpen}
          onClose={() => setUpdateVersionModalOpen(false)}
        />
      )}
      <main className={classes.content}>
        <AuditModeBanner />
        {children ? children : null}
      </main>
      <AnnouncementsOnLogin />
    </div>
  );
};

export default LoggedInLayout;
